import clsx from 'clsx';
import { forwardRef } from 'react';

import {
  type DataTestId,
  type MaskingProps,
  type StylingProps,
  type WithChildren,
  type AriaLabelingProps,
} from '@dynatrace/strato-components/core';
import { Flex, Divider } from '@dynatrace/strato-components/layouts';
import { Heading } from '@dynatrace/strato-components/typography';

import { getHeadingVariant } from './empty-state-utils.js';
import { contentContainerCss, titleSlotCss } from './EmptyState.sty.js';
import { EmptyStateWrapper } from './EmptyStateWrapper.js';
import { useEmptyStateSlots } from './hooks/use-empty-state-slots.js';
import { Actions } from './slot-components/Actions.js';
import { Details } from './slot-components/Details.js';
import { Footer } from './slot-components/Footer.js';
import { Title } from './slot-components/Title.js';
import { Visual } from './slot-components/Visual.js';
import { VisualPreset } from './slot-components/VisualPreset.js';

//#region Typings

/**
 * @public
 */
export interface EmptyStateProps
  extends WithChildren,
    AriaLabelingProps,
    StylingProps,
    DataTestId,
    MaskingProps {
  /**
   * Size of the empty state component.
   * @defaultValue 'default'
   */
  size?: 'small' | 'default' | 'large';
}
//#endregion Typings

const BaseEmptyState = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  EmptyStateProps
>((props, forwardedRef) => {
  const {
    size = 'default',
    children,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
  } = props;

  const slots = useEmptyStateSlots(children);
  const visualSlot = slots.Visual ? slots.Visual : null;
  const detailsSlot = slots.Details ? slots.Details : null;
  const actionsSlot = slots.Actions ? slots.Actions : null;
  const footerSlot = slots.Footer ? slots.Footer : null;
  const titleSlot = slots.Title ? (
    <Heading
      {...getHeadingVariant(size, slots.Title.props.as)}
      className={clsx(titleSlotCss)}
    >
      {slots.Title}
    </Heading>
  ) : null;

  const hasDivider =
    (slots.Actions || slots.Details || slots.Title || slots.Visual) &&
    slots.Footer;

  const divider = hasDivider ? <Divider /> : null;

  return (
    <EmptyStateWrapper
      size={size}
      ref={forwardedRef}
      className={consumerClassName}
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      style={consumerStyle}
    >
      {visualSlot}
      <Flex
        className={clsx(contentContainerCss)}
        maxWidth={600}
        flexDirection="column"
        gap={0}
      >
        {titleSlot}
        {detailsSlot}
        {actionsSlot}
        {divider}
        {footerSlot}
      </Flex>
    </EmptyStateWrapper>
  );
});

/**
 * Use the `EmptyState` component to give feedback to an
 * end-user in case there is no data available. There are cases where this data is
 * missing, requires special permission, or has not been yet created, for example.
 * @public
 */
export const EmptyState = Object.assign(BaseEmptyState, {
  Actions,
  Details,
  Footer,
  Title,
  Visual,
  VisualPreset,
});
